/**
 * PCM program pane on the patient profile — overview + PCM-only care plans + follow-ups.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faPencilAlt,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { formatFollowUpNoteFooter } from '../../../../utils/followUpDisplayUtils';
import { normalizePatientChronicConditions } from '../../../../utils/patientChronicConditionUtils';
import ActionButtons from '../../../../components/Common/ActionButtons/ActionButtons';
import ListScreenPaginationBar from '../../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import PCMCarePlanPanel from './PCMCarePlanPanel';

function displayPlanDate(value, formatDateShort) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (typeof formatDateShort === 'function') {
    const formatted = formatDateShort(raw);
    return formatted && formatted !== '-' ? formatted : '';
  }
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}-${match[3]}-${match[1]}` : raw;
}

const SUB_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'followup', label: 'Follow up' },
];

function getActionMenuCoords(buttonEl) {
  if (!buttonEl) return null;
  const rect = buttonEl.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const right = Math.max(8, vw - rect.right);
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
  if (openUp) {
    return {
      bottom: Math.max(8, vh - rect.top + 4),
      right,
    };
  }
  return {
    top: rect.bottom + 4,
    right,
  };
}

const PatientPcmProgramPane = ({
  patient,
  enrollmentSummary,
  formatDateShort,
  programTabs,
  onBillingRefresh,
  followUpNotes = [],
  onAddFollowUp,
  onEditFollowUp,
  isPatientSelfView = false,
  practiceCaregivers = [],
  practiceProviders = [],
  currentUser = null,
}) => {
  const [subTab, setSubTab] = useState('overview');
  const [followUpPage, setFollowUpPage] = useState(1);
  const [followUpEntries, setFollowUpEntries] = useState('10');
  const [savedPlans, setSavedPlans] = useState([]);
  const [openActionIndex, setOpenActionIndex] = useState(null);
  const [menuCoords, setMenuCoords] = useState(null);
  const activeBtnRef = useRef(null);
  const dropdownRef = useRef(null);
  const planRef = useRef(null);
  const handlePlansChange = useCallback((nextPlans) => {
    setSavedPlans(Array.isArray(nextPlans) ? nextPlans : []);
  }, []);

  const handleToggleMenu = (e, index) => {
    e.stopPropagation();
    if (openActionIndex === index) {
      setOpenActionIndex(null);
      setMenuCoords(null);
      activeBtnRef.current = null;
    } else {
      const btn = e.currentTarget;
      activeBtnRef.current = btn;
      setMenuCoords(getActionMenuCoords(btn));
      setOpenActionIndex(index);
    }
  };

  useEffect(() => {
    if (openActionIndex === null) return undefined;

    const updatePosition = () => {
      if (activeBtnRef.current) {
        const rect = activeBtnRef.current.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
          setOpenActionIndex(null);
          setMenuCoords(null);
          activeBtnRef.current = null;
        } else {
          setMenuCoords(getActionMenuCoords(activeBtnRef.current));
        }
      }
    };

    const handleOutsideClick = (e) => {
      if (activeBtnRef.current && activeBtnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpenActionIndex(null);
      setMenuCoords(null);
      activeBtnRef.current = null;
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openActionIndex]);

  const enrollmentId = patient?.ccm_enrollment_id || patient?.id;
  const conditions = normalizePatientChronicConditions(patient?.icd_codes);
  const principal = conditions[0] || null;
  const principalLabel = principal
    ? [principal.icd_code, principal.condition_name].filter(Boolean).join(' · ')
    : '';
  const carePlanApproved = Boolean(enrollmentSummary?.has_approved_care_plan);
  const carePlanStatus = enrollmentSummary?.care_plan_status;
  const carePlanName = enrollmentSummary?.care_plan_name;
  const isPlaceholderPlan = !carePlanName || carePlanName === 'Principal Care Management Plan';
  const carePlanStarted = !isPlaceholderPlan && (
    carePlanApproved
    || carePlanStatus === 'draft'
    || carePlanStatus === 'pending'
    || carePlanStatus === 'active'
  );
  const carePlanLead = !carePlanStarted
    ? 'Not started'
    : carePlanApproved
      ? 'Active'
      : carePlanStatus === 'pending'
        ? 'Pending'
        : carePlanStatus === 'draft'
          ? 'Pending'
          : 'Unsigned';
  const carePlanNote = !carePlanStarted
    ? 'required for PCM billing'
    : carePlanApproved
      ? (carePlanName || 'ready for PCM billing')
      : 'provider must sign and approve for billing';

  const sortedFollowUpNotes = useMemo(() => {
    const list = Array.isArray(followUpNotes) ? [...followUpNotes] : [];
    return list.sort((a, b) => {
      const dateA = new Date(a.date || a.created_at || 0).getTime();
      const dateB = new Date(b.date || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [followUpNotes]);

  const paginatedFollowUpNotes = useMemo(() => {
    if (followUpEntries === 'all' || followUpEntries === 'All') {
      return sortedFollowUpNotes;
    }
    const limit = Number.parseInt(followUpEntries, 10) || 10;
    const startIndex = (followUpPage - 1) * limit;
    return sortedFollowUpNotes.slice(startIndex, startIndex + limit);
  }, [sortedFollowUpNotes, followUpPage, followUpEntries]);

  return (
    <div className="pd-ccm-root">
      <div className="patient-details-sub-tabs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {SUB_TABS.map((t) => (
            <button
              key={t.key}
              className={`patient-details-sub-tab ${subTab === t.key ? 'active' : ''}`}
              onClick={() => setSubTab(t.key)}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
          {programTabs}
        </div>
      </div>

      <div className="pd-ccm-content">
        {subTab === 'overview' && (
          <div className="pcm-summary pcm-summary--panel">
            <div className="pcm-principal">
              <span className="pcm-summary-label">Principal condition</span>
              {principalLabel ? (
                <span className="pcm-summary-pill">{principalLabel}</span>
              ) : (
                <span className="pcm-summary-muted">No principal condition yet</span>
              )}
            </div>
            <div className="pcm-plan-head">
              <span className="pcm-summary-label">Care plan</span>
              {!isPatientSelfView ? (
                <button type="button" className="pcm-summary-create" onClick={() => planRef.current?.openCreate()}>
                  Create care plan
                </button>
              ) : null}
            </div>
            {savedPlans.length === 0 ? (
              <div className="pcm-plan-card">
                <div className="pcm-plan-card-copy">
                  <span className="pcm-plan-badge">{carePlanLead}</span>
                  <p>{carePlanNote}</p>
                </div>
              </div>
            ) : savedPlans.map((plan, index) => {
              const planStatus = plan.status === 'active'
                ? 'Active'
                : plan.status === 'pending'
                  ? 'Pending'
                  : 'Not saved';
              const planTitle = plan.diagnosis || plan.label || 'Care plan';
              const planNote = plan.status === 'active'
                ? 'Approved and ready for PCM billing.'
                : plan.status === 'pending'
                  ? 'Waiting for the provider to approve this care plan.'
                  : 'Finish and save this care plan.';
              const canApprove = plan.status === 'pending'
                && plan.planId
                && Number(currentUser?.role_id) === 4
                && !isPatientSelfView;
              const planFacts = [
                ['Start date', displayPlanDate(plan.startDate, formatDateShort)],
                ['Next review', displayPlanDate(plan.nextReview, formatDateShort)],
                ['Review interval', plan.reviewInterval],
                ['Provider', plan.provider],
                ['Caregivers', plan.caregivers],
                ['Diagnosis', plan.diagnosis && plan.diagnosis !== planTitle ? plan.diagnosis : ''],
              ].filter(([, value]) => String(value || '').trim());
              return (
              <div className={`pcm-plan-card is-${plan.status || 'unsaved'}`} key={plan.instanceId || index}>
                <div className="pcm-plan-card-copy">
                  <span className="pcm-plan-badge">{planStatus}</span>
                  <strong>{planTitle}</strong>
                  <p>{planNote}</p>
                </div>
                {planFacts.length > 0 ? (
                  <dl className="pcm-plan-facts">
                    {planFacts.map(([label, value]) => (
                      <div className="pcm-plan-fact" key={label}>
                        <dt>{label}</dt>
                        <dd title={value}>{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
                <div className="pcm-summary-actions">
                  {canApprove ? (
                    <>
                      <button type="button" className="pcm-summary-reject" onClick={() => planRef.current?.rejectPlan(index)}>
                        Reject
                      </button>
                      <button type="button" className="pcm-summary-create" onClick={() => planRef.current?.approvePlan(index)}>
                        Approve
                      </button>
                    </>
                  ) : null}
                  <div className="pcm-action-menu-wrap">
                    <button
                      type="button"
                      className="pcm-action-dots-btn"
                      onClick={(e) => handleToggleMenu(e, index)}
                      aria-label="Actions"
                      title="Actions"
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {openActionIndex !== null && menuCoords && typeof document !== 'undefined'
          ? createPortal(
              <div
                ref={dropdownRef}
                className="pcm-action-menu-dropdown"
                style={{
                  position: 'fixed',
                  top: menuCoords.top !== undefined ? `${menuCoords.top}px` : undefined,
                  bottom: menuCoords.bottom !== undefined ? `${menuCoords.bottom}px` : undefined,
                  right: `${menuCoords.right}px`,
                  zIndex: 999999,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="pcm-action-menu-item"
                  onClick={() => {
                    const idx = openActionIndex;
                    setOpenActionIndex(null);
                    setMenuCoords(null);
                    activeBtnRef.current = null;
                    planRef.current?.viewPlan(idx);
                  }}
                >
                  View
                </button>
                {!isPatientSelfView ? (
                  <button
                    type="button"
                    className="pcm-action-menu-item"
                    onClick={() => {
                      const idx = openActionIndex;
                      setOpenActionIndex(null);
                      setMenuCoords(null);
                      activeBtnRef.current = null;
                      planRef.current?.editPlan(idx);
                    }}
                  >
                    Edit
                  </button>
                ) : null}
                <button
                  type="button"
                  className="pcm-action-menu-item"
                  onClick={() => {
                    const idx = openActionIndex;
                    setOpenActionIndex(null);
                    setMenuCoords(null);
                    activeBtnRef.current = null;
                    planRef.current?.printPlan(idx);
                  }}
                >
                  Print
                </button>
                <button
                  type="button"
                  className="pcm-action-menu-item"
                  onClick={() => {
                    const idx = openActionIndex;
                    setOpenActionIndex(null);
                    setMenuCoords(null);
                    activeBtnRef.current = null;
                    planRef.current?.downloadPlan(idx);
                  }}
                >
                  Download
                </button>
                {!isPatientSelfView ? (
                  <button
                    type="button"
                    className="pcm-action-menu-item pcm-action-menu-item--delete"
                    onClick={() => {
                      const idx = openActionIndex;
                      setOpenActionIndex(null);
                      setMenuCoords(null);
                      activeBtnRef.current = null;
                      planRef.current?.deletePlan(idx);
                    }}
                  >
                    Delete
                  </button>
                ) : null}
              </div>,
              document.body
            )
          : null}

        <div className="pd-ccm-panel" style={{ display: subTab === 'careplan' ? undefined : 'none' }}>
          <PCMCarePlanPanel
            ref={planRef}
            enrollmentId={String(enrollmentSummary?.enrollment_id || enrollmentId)}
            patient={patient}
            readOnly={isPatientSelfView}
            practiceCaregivers={practiceCaregivers}
            practiceProviders={practiceProviders}
            onBillingRefresh={onBillingRefresh}
            onPlansChange={handlePlansChange}
            canReview={Number(currentUser?.role_id) === 4 && !isPatientSelfView}
          />
        </div>

        {subTab === 'followup' && (
          <div className="patient-details-sub-pane">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f2942' }}>
                  Follow-up Notes — PCM
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>All patient follow-ups and care team entries for PCM</span>
              </div>
              {!isPatientSelfView && onAddFollowUp ? (
                <button
                  type="button"
                  className="patient-details-btn-primary"
                  onClick={onAddFollowUp}
                  style={{
                    padding: '9px 16px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: '#023d3d',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Follow Up
                </button>
              ) : null}
            </div>

            <div className="patient-details-notes-list-new">
              {paginatedFollowUpNotes && paginatedFollowUpNotes.length > 0 ? (
                paginatedFollowUpNotes.map((note, index) => (
                  <div
                    key={note.id || index}
                    className="patient-details-note-item-new"
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '14px',
                      marginBottom: '12px',
                    }}
                  >
                    <div className="note-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span className="author" style={{ fontWeight: 700, color: '#0f2942', fontSize: '13px' }}>
                        {note.author_name || `${note.last_name || ''}, ${note.first_name || ''}`.trim() || 'Unknown'}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className="date" style={{ fontSize: '12px', color: '#64748b' }}>
                          {note.date ? formatDateShort(note.date) : formatDateShort(note.created_at)}
                        </span>
                        {!isPatientSelfView && onEditFollowUp ? (
                          <button
                            type="button"
                            className="patient-details-btn-edit-note"
                            onClick={() => onEditFollowUp(note)}
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              border: '1px solid #dce4ec',
                              background: 'transparent',
                              color: '#64748b',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '13px',
                              padding: 0,
                              transition: 'all 0.15s ease',
                            }}
                            title="Edit Follow Up"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <p className="note-content" style={{ margin: '6px 0', fontSize: '13.5px', color: '#334155' }}>
                      {note.content || ''}
                    </p>
                    <div className="note-footer" style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                      {formatFollowUpNoteFooter(note)}
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className="patient-details-note-item-new"
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0',
                  }}
                >
                  <p className="text-muted" style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                    No PCM follow-up notes found for this patient.
                  </p>
                </div>
              )}
            </div>

            {sortedFollowUpNotes.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <ListScreenPaginationBar
                  entriesValue={followUpEntries}
                  onEntriesChange={(val) => {
                    setFollowUpEntries(val);
                    setFollowUpPage(1);
                  }}
                  currentPage={followUpPage}
                  totalRecords={sortedFollowUpNotes.length}
                  onPageChange={(page) => setFollowUpPage(page)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientPcmProgramPane;

